import { motion, useScroll, AnimatePresence } from 'motion/react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { Button } from './Button';
import { useState, useEffect } from 'react';
import {
  ChevronDown,
  FileText,
  ShoppingCart,
  Activity,
  Code,
  Coins,
  Target,
  CreditCard,
  Cloud,
  Building2,
  LifeBuoy,
  Shield,
  Users,
  Menu,
  X,
  RadioTower,
  Network,
} from 'lucide-react';

const products = [
  {
    name: 'Digital Commerce Platform',
    desc: 'Scale Cloud and Telecom Services Revenue',
    icon: ShoppingCart,
    path: '/products/marketplace',
  },
  {
    name: 'CPQ- Configure Price Quote',
    desc: 'Intelligent quote-to-revenue acceleration',
    icon: Target,
    path: '/products/revsimply',
  },
  {
    name: 'RMS- Renewal Management System',
    desc: 'Managed renewals & recurring revenue',
    icon: CreditCard,
    path: '/products/rms',
  },
];

const capabilities = [
  {
    name: 'Lead to Order (L2O)',
    desc: 'Convert leads into validated quotes and order-ready workflows',
    icon: Target,
    path: '/platform/lead-to-order',
  },
  {
    name: 'Order to Cash (O2C)',
    desc: 'Automate orders, billing, payments, collections, and revenue sync',
    icon: CreditCard,
    path: '/platform/order-to-cash',
  },
  {
    name: 'Ticket to Resolution (T2R)',
    desc: 'Resolve customer issues with intelligent intake, routing, and SLA control',
    icon: LifeBuoy,
    path: '/platform/ticket-to-resolution',
  },
];

const services = [
  {
    name: 'Revenue Assurance',
    desc: 'Revenue leakage detection and reconciliation controls',
    icon: Coins,
    path: '/services/revenue-assurance',
  },
  {
    name: 'Process Control',
    desc: 'Governed workflows, SLA controls, and policy enforcement',
    icon: Activity,
    path: '/services/process-control',
  },
  {
    name: 'Managed Support Desk',
    desc: 'Lifecycle support operations and intelligent ticket routing',
    icon: LifeBuoy,
    path: '/services/managed-support-desk',
  },
  {
    name: 'Consulting & Custom Development',
    desc: 'Transformation consulting and custom platform engineering',
    icon: Code,
    path: '/services/consulting-custom-development',
  },
];

const solutionGroups = [
  {
    name: 'Telco Digital Monetization',
    desc: 'Transform connectivity businesses into digital service providers.',
    icon: RadioTower,
    path: '/industries/telco-to-techco',
    useCases: [
      'Cloud Marketplace',
      'UCaaS Monetization',
      'CCaaS Monetization',
      'CPaaS Monetization',
      'AI Marketplace',
      'Partner Ecosystem',
    ],
  },
  {
    name: 'Cloud Distribution',
    desc: 'For distributors such as Redington, Ingram, Westcon, TD Synnex.',
    icon: Building2,
    path: '/industries/cloud-distribution',
    useCases: [
      'Cloud Marketplace',
      'CSP Billing',
      'Multi-tier Channel Automation',
      'Microsoft/AWS Distribution',
    ],
  },
  {
    name: 'ISV Monetization',
    desc: 'For software vendors.',
    icon: Cloud,
    path: '/industries/saas-isv',
    useCases: [
      'Subscription Billing',
      'License Management',
      'Marketplace Syndication',
      'Partner Distribution',
    ],
  },
];

const company = [
  {
    name: 'About Us',
    desc: 'Our vision, mission, and the team',
    icon: Users,
    path: '/platform/about',
  },
  {
    name: 'Case Studies',
    desc: 'Customer outcomes and platform transformation stories',
    icon: FileText,
    path: '/case-studies',
  },
  {
    name: 'Integrations',
    desc: 'Available AppGallop APIs',
    icon: Network,
    path: '/platform/integrations',
  },
  {
    name: 'Security',
    desc: 'Enterprise-grade protection',
    icon: Shield,
    path: '/platform/security',
  },
];

export function Navbar() {
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const menuIconBoxClass =
    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#334155] transition-colors group-hover:bg-[#ff5d22]/10 group-hover:text-[#ff5d22]';

  useEffect(() => {
    const unsubscribe = scrollY.on('change', (v) => {
      setIsScrolled(v > 50);
    });

    return () => unsubscribe();
  }, [scrollY]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const MegaMenu = ({ items, isOpen }: { items: any[]; isOpen: boolean }) => (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`absolute top-full left-1/2 -translate-x-1/2 mt-0 ${
            items.length === 3
              ? 'w-[800px] grid-cols-3'
              : items.length === 5
              ? 'w-[1100px] grid-cols-5'
              : items.length === 4
              ? 'w-[900px] grid-cols-4'
              : 'w-[600px] grid-cols-2'
          } bg-white rounded-[2.5rem] shadow-2xl border border-black/5 p-8 grid gap-2`}
        >
          {items.map((item, i) => (
            <Link
              key={i}
              to={item.path}
              className="flex flex-col items-start gap-4 p-5 rounded-2xl hover:bg-slate-50 transition-colors group"
            >
              <div className={menuIconBoxClass}>
                <item.icon size={20} />
              </div>

              <div>
                <div className="text-[11px] font-black capitalize tracking-widest text-black group-hover:text-[#0F172A] transition-colors mb-1">
                  {item.name}
                </div>
                <div className="text-[10px] font-bold text-black/40 leading-tight">
                  {item.desc}
                </div>
              </div>
            </Link>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const PlatformMegaMenu = ({ isOpen }: { isOpen: boolean }) => (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-0 w-[980px] bg-white rounded-[2.5rem] shadow-2xl border border-black/5 p-8 grid grid-cols-2 gap-8"
        >
          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-[#ff5d22] mb-4">
              By Product
            </div>

            <div className="grid gap-2">
              {products.map((item, i) => (
                <Link
                  key={i}
                  to={item.path}
                  className="flex items-start gap-4 p-5 rounded-2xl hover:bg-slate-50 transition-colors group"
                >
                  <div className={menuIconBoxClass}>
                    <item.icon size={20} />
                  </div>

                  <div>
                    <div className="text-[11px] font-black capitalize tracking-widest text-black group-hover:text-[#0F172A] transition-colors mb-1">
                      {item.name}
                    </div>
                    <div className="text-[10px] font-bold text-black/40 leading-tight">
                      {item.desc}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-[#ff5d22] mb-4">
              By Capabilities
            </div>

            <div className="grid gap-2">
              {capabilities.map((item, i) => (
                <Link
                  key={i}
                  to={item.path}
                  className="flex items-start gap-4 p-5 rounded-2xl hover:bg-slate-50 transition-colors group"
                >
                  <div className={menuIconBoxClass}>
                    <item.icon size={20} />
                  </div>

                  <div>
                    <div className="text-[11px] font-black capitalize tracking-widest text-black group-hover:text-[#0F172A] transition-colors mb-1">
                      {item.name}
                    </div>
                    <div className="text-[10px] font-bold text-black/40 leading-tight">
                      {item.desc}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const SolutionsMegaMenu = ({ isOpen }: { isOpen: boolean }) => (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-0 grid w-[1080px] grid-cols-3 gap-4 rounded-[2.5rem] border border-black/5 bg-white p-8 shadow-2xl"
        >
          {solutionGroups.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className="group flex min-h-[260px] flex-col rounded-2xl border border-black/6 bg-white p-5 transition-all hover:-translate-y-1 hover:border-[#ff5d22]/25 hover:bg-slate-50 hover:shadow-xl"
            >
              <div className="flex items-start gap-4">
                <div className={menuIconBoxClass}>
                  <item.icon size={20} />
                </div>
                <div>
                  <div className="text-[12px] font-black tracking-widest text-black transition-colors group-hover:text-[#0F172A]">
                    {item.name}
                  </div>
                  <div className="mt-2 text-[10px] font-bold leading-relaxed text-black/45">
                    {item.desc}
                  </div>
                </div>
              </div>

              <div className="mt-5 border-t border-black/6 pt-4">
                <div className="mb-3 text-[9px] font-black uppercase tracking-[0.18em] text-[#ff5d22]">
                  Use Cases
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.useCases.map((useCase) => (
                    <span
                      key={useCase}
                      className="rounded-full border border-black/8 bg-white px-2.5 py-1 text-[9px] font-black leading-tight text-slate-500 transition-colors group-hover:border-[#ff5d22]/20 group-hover:text-slate-700"
                    >
                      {useCase}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-[5000] border-b transition-all duration-500 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-2xl border-black/5 py-3 shadow-sm'
            : 'bg-white border-transparent py-4'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-8 xl:px-12">
          <div className="flex items-center justify-between">
            <Link to="/" className="hover:scale-105 transition-transform duration-500">
              <Logo />
            </Link>

          <div className="hidden xl:flex items-center gap-7 2xl:gap-10 text-[12px] font-black capitalize tracking-widest text-black/40">
            <Link
              to="/"
              className={`hover:text-black transition-colors relative group py-2 ${
                location.pathname === '/' ? 'text-black' : ''
              }`}
            >
              Home
              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-[#ff5d22] transition-all duration-500 ${
                  location.pathname === '/' ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              />
            </Link>

            <div
              className="relative py-2 cursor-pointer group"
              onMouseEnter={() => setHoveredMenu('platform')}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <div className="flex items-center gap-2 group-hover:text-black transition-colors">
                Platform
                <ChevronDown
                  size={12}
                  className={`transition-transform duration-300 ${
                    hoveredMenu === 'platform' ? 'rotate-180' : ''
                  }`}
                />
              </div>

              <PlatformMegaMenu isOpen={hoveredMenu === 'platform'} />

              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5d22] transition-all duration-500 group-hover:w-full" />
            </div>

            <div
              className={`relative py-2 cursor-pointer group ${
                location.pathname.startsWith('/services') || location.pathname.startsWith('/solutions')
                  ? 'text-black'
                  : ''
              }`}
              onMouseEnter={() => setHoveredMenu('services')}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <div className="flex items-center gap-2 group-hover:text-black transition-colors">
                Services
                <ChevronDown
                  size={12}
                  className={`transition-transform duration-300 ${
                    hoveredMenu === 'services' ? 'rotate-180' : ''
                  }`}
                />
              </div>

              <MegaMenu items={services} isOpen={hoveredMenu === 'services'} />

              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-[#ff5d22] transition-all duration-500 ${
                  location.pathname.startsWith('/services') || location.pathname.startsWith('/solutions')
                    ? 'w-full'
                    : 'w-0 group-hover:w-full'
                }`}
              />
            </div>

            <div
              className={`relative py-2 cursor-pointer group ${
                location.pathname.startsWith('/industries') ? 'text-black' : ''
              }`}
              onMouseEnter={() => setHoveredMenu('industries')}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <div className="flex items-center gap-2 group-hover:text-black transition-colors">
                Solutions
                <ChevronDown
                  size={12}
                  className={`transition-transform duration-300 ${
                    hoveredMenu === 'industries' ? 'rotate-180' : ''
                  }`}
                />
              </div>

              <SolutionsMegaMenu isOpen={hoveredMenu === 'industries'} />

              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-[#ff5d22] transition-all duration-500 ${
                  location.pathname.startsWith('/industries') ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              />
            </div>

            <div
              className="relative py-2 cursor-pointer group"
              onMouseEnter={() => setHoveredMenu('company')}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <div className="flex items-center gap-2 group-hover:text-black transition-colors">
                Company
                <ChevronDown
                  size={12}
                  className={`transition-transform duration-300 ${
                    hoveredMenu === 'company' ? 'rotate-180' : ''
                  }`}
                />
              </div>

              <MegaMenu items={company} isOpen={hoveredMenu === 'company'} />

              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5d22] transition-all duration-500 group-hover:w-full" />
            </div>
          </div>

            <div className="flex items-center gap-4 lg:gap-6">
              <div className="hidden sm:block">
                <Link to="/contact">
                  <Button size="sm">Request a Demo</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      <button
        type="button"
        onClick={() => setIsMobileMenuOpen((open) => !open)}
        className="xl:hidden fixed right-4 top-3 z-[11000] flex h-11 w-11 items-center justify-center rounded-full border border-black/5 bg-slate-100 text-[#334155] shadow-xl transition-colors hover:bg-[#ff5d22]/10 hover:text-[#ff5d22] sm:right-6 sm:top-4"
        aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isMobileMenuOpen}
      >
        {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="mobile-nav-panel xl:hidden fixed inset-0 z-[10500] bg-white pt-[68px] sm:pt-[76px] shadow-2xl overflow-hidden"
          >
            <div className="mobile-nav-scroll h-full overflow-y-auto overscroll-contain responsive-scroll touch-pan-y px-5 sm:px-6 py-5 sm:py-6 pb-[calc(2rem+env(safe-area-inset-bottom))] space-y-7 sm:space-y-8">
              <div className="grid gap-6">
                <Link
                  to="/"
                  className="text-[14px] font-black capitalize tracking-widest text-black"
                >
                  Home
                </Link>

                <div className="space-y-4">
                  <div className="text-[11px] font-black capitalize tracking-widest text-black/30">
                    Platform
                  </div>

                  <div className="grid gap-4 pl-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-black/30">
                      By Product
                    </div>

                    {products.map((item, i) => (
                      <Link key={i} to={item.path} className="group flex items-center gap-3">
                        <div className={menuIconBoxClass}>
                          <item.icon size={16} />
                        </div>
                        <span className="text-[12px] font-black capitalize tracking-widest text-black leading-snug">
                          {item.name}
                        </span>
                      </Link>
                    ))}

                    <div className="text-[10px] font-black uppercase tracking-widest text-black/30 pt-3">
                      By Capabilities
                    </div>

                    {capabilities.map((item, i) => (
                      <Link key={i} to={item.path} className="group flex items-center gap-3">
                        <div className={menuIconBoxClass}>
                          <item.icon size={16} />
                        </div>
                        <span className="text-[12px] font-black capitalize tracking-widest text-black leading-snug">
                          {item.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="text-[11px] font-black capitalize tracking-widest text-black/30">
                    Services
                  </div>

                  <div className="grid gap-4 pl-2">
                    {services.map((item, i) => (
                      <Link key={i} to={item.path} className="group flex items-center gap-3">
                        <div className={menuIconBoxClass}>
                          <item.icon size={16} />
                        </div>
                        <span className="text-[12px] font-black capitalize tracking-widest text-black leading-snug">
                          {item.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="text-[11px] font-black capitalize tracking-widest text-black/30">
                    Solutions
                  </div>

                  <div className="grid gap-4 pl-2">
                    {solutionGroups.map((item) => (
                      <Link key={item.name} to={item.path} className="group block rounded-2xl border border-black/6 bg-slate-50 p-4 transition-colors hover:bg-slate-100">
                        <div className="flex items-start gap-3">
                          <div className={menuIconBoxClass}>
                            <item.icon size={16} />
                          </div>
                          <div>
                            <span className="block text-[12px] font-black tracking-widest text-black leading-snug">
                              {item.name}
                            </span>
                            <span className="mt-1 block text-[11px] font-bold leading-relaxed text-black/45">
                              {item.desc}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5 pl-7">
                          {item.useCases.map((useCase) => (
                            <span
                              key={useCase}
                              className="rounded-full border border-black/8 bg-white px-2 py-1 text-[8px] font-black uppercase tracking-[0.08em] text-black/45"
                            >
                              {useCase}
                            </span>
                          ))}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="text-[11px] font-black capitalize tracking-widest text-black/30">
                    Company
                  </div>

                  <div className="grid gap-4 pl-2">
                    {company.map((item, i) => (
                      <Link key={i} to={item.path} className="group flex items-center gap-3">
                        <div className={menuIconBoxClass}>
                          <item.icon size={16} />
                        </div>
                        <span className="text-[12px] font-black capitalize tracking-widest text-black leading-snug">
                          {item.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-black/5">
                <Link to="/contact" className="block">
                  <Button className="w-full">Request a Demo</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
